import React from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import { useMinigridNamesByUuid } from '../service/useMinigridNamesByUuid'

// Sentinel option presented as "Tous" in the dropdown. Selecting it picks every
// real uuid; deselecting it clears the selection. Never leaves this component
// — `onChange` always receives real uuid strings only.
const ALL_SENTINEL = '__ALL__'
const ALL_LABEL = 'Tous'

export interface LocationMultiSelectProps {
  value: string[]
  onChange: (uuids: string[]) => void
}

export default function LocationMultiSelect({ value, onChange }: LocationMultiSelectProps) {
  const { resolveName, options: catalogOptions, isLoading } = useMinigridNamesByUuid()

  const options: string[] = catalogOptions.map((o) => o.uuid)
  const allSelected = options.length > 0 && options.every((o) => value.includes(o))
  const augmentedOptions = [ALL_SENTINEL, ...options]
  const augmentedValue = allSelected ? [ALL_SENTINEL, ...value] : value

  const handleChange = (_e: unknown, newValue: string[]) => {
    const sentinelInNew = newValue.includes(ALL_SENTINEL)
    if (sentinelInNew && !allSelected) {
      onChange([...options])
      return
    }
    if (!sentinelInNew && allSelected) {
      onChange([])
      return
    }
    onChange(newValue.filter((v) => v !== ALL_SENTINEL))
  }

  return (
    <Autocomplete
      multiple
      freeSolo={false}
      options={augmentedOptions}
      value={augmentedValue}
      loading={isLoading}
      loadingText="Chargement..."
      noOptionsText="Aucun minigrid disponible"
      getOptionLabel={(option) => (option === ALL_SENTINEL ? ALL_LABEL : resolveName(option))}
      onChange={handleChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue
          .filter((opt) => opt !== ALL_SENTINEL)
          .map((option, index) => (
            <Chip
              label={resolveName(option)}
              size="small"
              {...getTagProps({ index })}
              key={option}
            />
          ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Minigrids"
          placeholder={value.length === 0 ? 'Sélectionnez des minigrids' : undefined}
          size="small"
        />
      )}
      style={{ minWidth: 320 }}
    />
  )
}
